import * as React from "react";
import { cn } from "@/lib/utils";

export interface InteractiveTitleProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Click handler that opens the item's detail/view page.
   * Mirrors the "View" action in the Actions dropdown.
   */
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  /**
   * Optional accessible name override. Defaults to the button text content.
   */
  "aria-label"?: string;
}

/**
 * A link-styled <button> meant to wrap an item's name/title in lists and cards.
 *
 * - Renders inside an existing heading element so heading semantics are preserved.
 * - Hover/focus styles match our standard interactive text affordance.
 * - Stops propagation so it can sit inside clickable parents without conflicts.
 */
export const InteractiveTitle = React.forwardRef<
  HTMLButtonElement,
  InteractiveTitleProps
>(({ className, onClick, children, ...rest }, ref) => {
  return (
    <button
      ref={ref}
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick(e);
      }}
      className={cn(
        "text-left bg-transparent p-0 m-0 truncate max-w-full",
        "cursor-pointer transition-colors",
        "hover:text-primary hover:underline underline-offset-2",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm",
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
});
InteractiveTitle.displayName = "InteractiveTitle";
